
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plane, Trash2, Clock, Users, MapPin, Edit, PlaneTakeoff, PlaneLanding, Route } from "lucide-react";
import type { PackageData } from '@/pages/CreatePackage';
import { EditFlightDialog } from './EditFlightDialog';

interface PackageFlightsListProps {
  packageFlights: PackageData['flights'];
  onRemoveFlight: (index: number) => void;
  onUpdateFlight: (index: number, flight: PackageData['flights'][0]) => void;
}

export const PackageFlightsList = ({ packageFlights, onRemoveFlight, onUpdateFlight }: PackageFlightsListProps) => {
  if (packageFlights.length === 0) {
    return (
      <Card className="border-2 border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50">
        <CardContent className="p-8 text-center">
          <div className="mx-auto mb-6 w-24 h-24 bg-gradient-to-br from-blue-100 to-sky-100 rounded-full flex items-center justify-center">
            <Plane className="w-12 h-12 text-blue-400" />
          </div>
          <p className="text-lg font-medium text-gray-700 mb-2">لم يتم إضافة أي رحلات طيران بعد</p>
          <p className="text-sm text-gray-500">ابدأ بإضافة رحلات الطيران الخاصة بالعرض السياحي</p>
        </CardContent>
      </Card>
    );
  }

  const getFlightTypeData = (type: string) => {
    const types = {
      outbound: { 
        label: 'رحلة ذهاب', 
        icon: PlaneTakeoff, 
        color: 'bg-blue-500 text-white',
        bgGradient: 'from-blue-50 to-blue-100',
        borderColor: 'border-blue-200'
      },
      return: { 
        label: 'رحلة عودة', 
        icon: PlaneLanding, 
        color: 'bg-green-500 text-white',
        bgGradient: 'from-green-50 to-green-100',
        borderColor: 'border-green-200'
      },
      internal: { 
        label: 'رحلة داخلية', 
        icon: Route, 
        color: 'bg-purple-500 text-white',
        bgGradient: 'from-purple-50 to-purple-100',
        borderColor: 'border-purple-200'
      }
    };
    return types[type as keyof typeof types] || types.outbound;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-br from-blue-500 to-sky-500 rounded-lg">
          <Plane className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-xl font-bold text-gray-800">رحلات الطيران المضافة</h3>
        <div className="flex-1 h-px bg-gradient-to-r from-blue-200 via-sky-200 to-transparent"></div>
        <Badge variant="secondary" className="bg-blue-100 text-blue-800 px-3 py-1">
          {packageFlights.length} رحلة
        </Badge>
      </div>

      <div className="space-y-4">
        {packageFlights.map((flight, index) => {
          const flightTypeData = getFlightTypeData(flight.type);
          const TypeIcon = flightTypeData.icon;
          
          return (
            <Card 
              key={index} 
              className={`relative overflow-hidden border-2 ${flightTypeData.borderColor} bg-gradient-to-r ${flightTypeData.bgGradient} hover:shadow-lg transition-all duration-300 hover:scale-[1.02]`}
            >
              {/* Header with flight route */}
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 ${flightTypeData.color} rounded-xl shadow-lg`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <CardTitle className="text-xl flex items-center gap-3">
                        <span className="font-bold text-gray-800">{flight.from}</span>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                          <div className="w-16 h-0.5 bg-gray-300"></div>
                          <Plane className="w-4 h-4 text-gray-400" />
                          <div className="w-16 h-0.5 bg-gray-300"></div>
                          <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                        </div>
                        <span className="font-bold text-gray-800">{flight.to}</span>
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={flightTypeData.color}>
                          {flightTypeData.label}
                        </Badge>
                        <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded-md shadow-sm">
                          {flight.airline}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <EditFlightDialog 
                      flight={flight} 
                      index={index} 
                      onUpdateFlight={onUpdateFlight} 
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRemoveFlight(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                {/* Flight details grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-500" />
                      <p className="text-sm font-medium text-gray-600">التاريخ والوقت</p>
                    </div>
                    <p className="font-bold text-gray-800">{flight.date}</p>
                    <p className="text-sm text-gray-600">{flight.time}</p>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Plane className="w-4 h-4 text-green-500" />
                      <p className="text-sm font-medium text-gray-600">درجة السفر</p>
                    </div>
                    <p className="font-bold text-gray-800">{flight.class}</p>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      <p className="text-sm font-medium text-gray-600">المسافرون</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm"><span className="font-medium">{flight.adults}</span> بالغ</p>
                      <p className="text-sm"><span className="font-medium">{flight.children}</span> طفل</p>
                      <p className="text-sm"><span className="font-medium">{flight.infants}</span> رضيع</p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                      <p className="text-sm font-medium text-gray-600">التسعير</p>
                    </div>
                    {flight.costPrice && (
                      <p className="text-xs text-gray-500 mb-1">التكلفة: {flight.costPrice} ر.س</p>
                    )}
                    <p className="font-bold text-green-600">{flight.sellingPrice || flight.price} ر.س</p>
                  </div>
                </div>

                {/* Additional info */}
                <div className="space-y-3">
                  {flight.transitFlight && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-600" />
                        <span className="font-medium text-yellow-800">رحلة ترانزيت</span>
                        <div className="h-4 w-px bg-yellow-300"></div>
                        <span className="text-sm text-yellow-700">مدة الانتظار: {flight.transitDuration} ساعة</span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    {flight.supplier && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-600">المورد:</span>
                        <span className="font-medium text-gray-800">{flight.supplier}</span>
                      </div>
                    )}

                    {flight.notes && (
                      <div className="flex items-start gap-2 max-w-full">
                        <div className="w-3 h-3 bg-blue-400 rounded-full mt-1 flex-shrink-0"></div>
                        <div>
                          <span className="text-gray-600 text-xs">ملاحظات:</span>
                          <p className="text-gray-800 text-sm break-words">{flight.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
